import { useDebouncedState, useDisclosure, useHotkeys } from "@mantine/hooks";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useDeferredValue, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { repositoryMdm, repositoryPos } from "../../../../_base/_const/_constVar";
import { MessageResponse } from "../../../../model/MessageResponse";
import { _localization } from "../../../../config/location";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Loader,
  LoadingOverlay,
  Menu,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import _breadcrumb from "../../../../_base/component/_layout/_breadcrumb";
import { IconDots, IconEdit, IconPlus, IconSearch } from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import CreateTransportConfiguration from "./CreateTransportConfiguration";
import EditTransportConfiguration from "./EditTransportConfiguration";
import { getProvinceSelectNew } from "../../../../service/getSelectApi";
import { getValueById } from "../../../../_base/helper/FunctionHelper";

type ColumnPinningType = {
  left?: string[];
  right?: string[];
};

const TransportConfiguration = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100,
  });
  const rowSelectionDeferred =
    useDeferredValue<MRT_RowSelectionState>(rowSelection);
  const [selectIds, setSelectIds] = useState<string[]>([]);
  const [visible, { toggle, open, close }] = useDisclosure(false);

  const navigate = useNavigate();
  const location = useLocation();
  const _searchJson = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";
  const [search, setSearch] = useState<any>({});
  const [keySearch, setKeySearch] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.keySearch : "",
    300,
  );

  const [dataProvince, setDataProvince] = useState<any[]>([]);
  const [dataCommune, setDataCommune] = useState<any[]>([]);
  const [listCommune, setListCommune] = useState<string[]>([]);
  const approvePW = [
    { label: "Áp dụng trên POS", value: "POS" },
    { label: "Áp dụng trên WEB", value: "WEB" },
    { label: "Áp dụng ở cả POS và WEB", value: "POS/WEB" },
  ];

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "code",
        header: "Mã loại GV",
        Cell: ({ renderedCellValue }) => (
          <Badge color="green" variant="dot">
            {renderedCellValue}
          </Badge>
        ),
      },
      { accessorKey: "name", header: "Tên loại GV" },
      {
        accessorKey: "attr13",
        header: "Trạng thái",
        Cell: ({ renderedCellValue }) => (
          <Badge
            color={Number(renderedCellValue) === 1 ? "green" : "red"}
            variant="dot"
          >
            {Number(renderedCellValue) === 1 ? "Hoạt động" : "Chưa hoạt động"}
          </Badge>
        ),
      },
      {
        accessorKey: "att1",
        header: "Tỉnh/TP áp dụng",
        size: 325,
        Cell: ({ renderedCellValue }) => {
          const dataRender =
            renderedCellValue && String(renderedCellValue).length > 0
              ? String(renderedCellValue).trim().split(",")
              : null;

          return dataRender && dataRender.length > 0 ? (
            dataRender.length <= 2 ? (
              <Flex gap={"xs"}>
                {dataRender.map((item, index) => (
                  <Badge key={index} variant="outline">
                    {getValueById(String(item), dataProvince, "label")}
                  </Badge>
                ))}
              </Flex>
            ) : (
              <Flex gap={"xs"} align={"center"}>
                {dataRender.slice(0, 2).map((item, index) => (
                  <Badge key={index} variant="outline">
                    {getValueById(String(item), dataProvince, "label")}
                  </Badge>
                ))}
                <Menu width={dataRender.length > 8 ? 650 : ""}>
                  <Menu.Target>
                    <Tooltip label="Xem thêm">
                      <ActionIcon variant="outline" size={"xs"} radius={"xl"}>
                        <IconDots />
                      </ActionIcon>
                    </Tooltip>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Flex gap={"xs"} wrap={"wrap"}>
                      {dataRender
                        .slice(2, dataRender.length)
                        .map((item, index) => (
                          <Badge key={index} variant="outline">
                            {getValueById(String(item), dataProvince, "label")}
                          </Badge>
                        ))}
                    </Flex>
                  </Menu.Dropdown>
                </Menu>
              </Flex>
            )
          ) : (
            <></>
          );
        },
      },
      {
        accessorKey: "att5",
        header: "Thành phố lớn",
        size: 250,
        Cell: ({ renderedCellValue }) => {
          const dataRender =
            renderedCellValue && String(renderedCellValue).length > 0
              ? String(renderedCellValue).trim().split(",")
              : null;

          if (!dataRender || dataRender.length === 0) return <></>;

          return (
            <Flex gap={"xs"} wrap={"wrap"}>
              {dataRender.map((item, index) => (
                <Badge key={index} variant="outline" color="blue">
                  {getValueById(String(item), dataProvince, "label")}
                </Badge>
              ))}
            </Flex>
          );
        },
      },
      {
        accessorKey: "att3",
        header: "Phường nội thành",
        size: 350,
        Cell: ({ renderedCellValue }) => {
          const dataRender =
            renderedCellValue && String(renderedCellValue).length > 0
              ? String(renderedCellValue).trim().split(",")
              : null;

          if (!dataRender || dataRender.length === 0) return <></>;

          return dataRender.length <= 3 ? (
            <Flex gap={"xs"} wrap={"wrap"}>
              {dataRender.map((item, index) => (
                <Badge key={index} variant="outline" color="orange">
                  {getValueById(String(item), dataCommune, "label")}
                </Badge>
              ))}
            </Flex>
          ) : (
            <Flex gap={"xs"} align={"center"}>
              {dataRender.slice(0, 2).map((item, index) => (
                <Badge key={index} variant="outline" color="orange">
                  {getValueById(String(item), dataCommune, "label")}
                </Badge>
              ))}
              <Menu width={dataRender.length > 8 ? 650 : ""}>
                <Menu.Target>
                  <Tooltip label="Xem thêm">
                    <ActionIcon variant="outline" size={"xs"} radius={"xl"}>
                      <IconDots />
                    </ActionIcon>
                  </Tooltip>
                </Menu.Target>
                <Menu.Dropdown>
                  <Flex gap={"xs"} wrap={"wrap"}>
                    {dataRender.slice(2).map((item, index) => (
                      <Badge key={index} variant="outline" color="orange">
                        {getValueById(String(item), dataCommune, "label")}
                      </Badge>
                    ))}
                  </Flex>
                </Menu.Dropdown>
              </Menu>
            </Flex>
          );
        },
      },
      {
        accessorKey: "att4",
        header: "Số tiền đồng giá GHTK",
        Cell: ({ renderedCellValue }) => (
          <Text fw={500} size="12.5px">
            {Number(renderedCellValue).toLocaleString()} VNĐ
          </Text>
        ),
        size: 225,
      },
      {
        accessorKey: "att2",
        header: "Số tiền giới hạn",
        Cell: ({ renderedCellValue }) => (
          <Text fw={500} size="12.5px">
            {Number(renderedCellValue).toLocaleString()} VNĐ
          </Text>
        ),
      },

      {
        accessorKey: "att9",
        header: "Số km bắt đầu tính phí",
        size: 250,
        Cell: ({ renderedCellValue }) =>
          renderedCellValue || Number(renderedCellValue) === 0 ? (
            <Text fw={500} size="12.5px">
              {Number(renderedCellValue).toLocaleString()} km
            </Text>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "att10",
        header: "Số tiền/1km cộng thêm khi vượt mức",
        size: 300,
        Cell: ({ renderedCellValue }) =>
          renderedCellValue || Number(renderedCellValue) === 0 ? (
            <Text fw={500} size="12.5px">
              {Number(renderedCellValue).toLocaleString()} VNĐ
            </Text>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "attr11",
        header: "Thời gian giao hàng mặc định",
        size: 275,
      },
      {
        accessorKey: "attr12",
        header: "Khoảng cách tính từ địa chỉ KH đến chi nhánh",
        size: 350,
        Cell: ({ renderedCellValue }) =>
          renderedCellValue || Number(renderedCellValue) === 0 ? (
            <Text fw={500} size="12.5px">
              {Number(renderedCellValue).toLocaleString()} km
            </Text>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "attr16",
        header: "Loại vận chuyển",
      },
      {
        accessorKey: "att8",
        header: "Áp dụng POS/WEB",
        Cell: ({ renderedCellValue }) => (
          <Text fw={500} size="12.5px">
            {getValueById(String(renderedCellValue), approvePW, "label")}
          </Text>
        ),
      },
      {
        accessorKey: "action",
        header: "Thao tác",
        enableColumnActions: false,
        enableSorting: false,
        enableColumnFilter: false,
        size: 80,
        Cell: ({ row }) => (
          <Flex justify={"start"} gap={"xs"}>
            <Tooltip label="Chỉnh sửa">
              <ActionIcon
                variant="light"
                color="orange"
                onClick={() => modalsEditTransportConfiguration(row.original)}
              >
                <IconEdit size={16} />
              </ActionIcon>
            </Tooltip>
          </Flex>
        ),
      },
    ],
    [dataProvince, dataCommune, approvePW],
  );

  const modalsCreateTransportConfiguration = () => {
    modals.openConfirmModal({
      title: <Title order={5}>Tạo mới loại GV</Title>,
      size: "auto",
      children: <CreateTransportConfiguration onSuccess={fetchData} />,
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  };

  const modalsEditTransportConfiguration = (data: any) => {
    modals.openConfirmModal({
      title: <Title order={5}>Chỉnh sửa loại GV</Title>,
      size: "auto",
      children: (
        <EditTransportConfiguration data={data} onSuccess={fetchData} />
      ),
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  };

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      let url = `/api/v1/WebsiteDelivery/get-list?Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      if (keySearch.length > 0) {
        url += `&KeySearch=${keySearch?.trim()}`;
      }

      url += `&${params.toString()}`;

      const res = await repositoryPos.get<MessageResponse<any>>(url);

      if (res && res.success) {
        const result = res.data.filter((i: any) => i.attr16 !== "Khác");
        const dataListCommune = result.map((i: any) => i.att3).join(",");
        setListCommune([
          ...new Set(
            dataListCommune
              .split(",")
              .filter((i: any) => i && String(i).length > 0),
          ),
        ] as string[]);
        setData(result);
        setRowCount(res.totalCount);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      sessionStorage.setItem(
        String(location.pathname),
        JSON.stringify({
          ...search,
          keySearch: keySearch,
        }),
      );
    }
  };

  // const exportDataExcel = async () => {
  //   open();
  //   try {
  //     let url = `?Skip=${pagination?.pageIndex}&Take=${pagination.pageSize}`;

  //     const params = new URLSearchParams();
  //     Object.entries(search).forEach(([key, value]) => {
  //       if (Array.isArray(value) && value.length > 0) {
  //         value.forEach((item) => params.append(key, String(item)));
  //       } else if (typeof value === "string" && value.length > 0) {
  //         params.append(key, value);
  //       }
  //     });

  //     if (keySearch.length > 0) {
  //       url += `&KeySearch=${keySearch?.trim()}`;
  //     }

  //     url += `&${params.toString()}`;

  //     const baseUrl = `/api/v1/ItemComparison/export-excel${url}`;

  //     const _url = BASE_API_MDM + baseUrl;
  //     const token = localStorage.getItem("token");

  //     const response = await fetch(_url, {
  //       method: "GET",
  //       headers: {
  //         "Content-Type": "application/vnd.ms-excel",
  //         Authorization: `Bearer ${token?.replace(`"`, "").replace(`"`, "")}`,
  //       },
  //     });

  //     if (!response.ok) {
  //       NotificationExtension.Fails("Export Excel thất bại !");
  //     } else {
  //       const blob = await response.blob();
  //       const urlss = window.URL.createObjectURL(blob);
  //       const _name = new Date().toISOString().slice(0, 10);
  //       let filename = "danh_sach_voucher.xlsx";
  //       const link = document.createElement("a");
  //       link.href = urlss;
  //       link.download = filename;
  //       document.body.appendChild(link);
  //       link.click();
  //       link.remove();
  //       window.URL.revokeObjectURL(url);
  //       NotificationExtension.Success("Export Excel thành công !");
  //     }
  //   } catch (error) {
  //     console.log(error);
  //   } finally {
  //     close();
  //   }
  // };

  const fetchDataProvince = async () => {
    const res = await getProvinceSelectNew();

    if (res) {
      setDataProvince(
        res
          ?.filter((item) => item.id && item.name)
          ?.map((item) => ({
            value: String(item.id),
            label: String(item.name),
          })),
      );
    }
  };

  const fetchDataCommune = async () => {
    const url =
      listCommune && listCommune.length > 0
        ? listCommune.map((i) => `Ids=${i}`).join("&")
        : "";
    const res = await repositoryMdm.get<MessageResponse<any>>(
      `/api/v1/TblDmCommuneMoi/get-select?${url}`,
    );

    if (res && res.success) {
      const result = res.data
        ?.filter((i: any) => i.text && i.value)
        ?.map((i: any) => ({
          label: String(i.text),
          value: String(i.value),
        }));
      setDataCommune(result);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination?.pageIndex, pagination?.pageSize]);

  useEffect(() => {
    fetchDataProvince();
  }, []);

  useEffect(() => {
    if (listCommune && listCommune?.length > 0) {
      fetchDataCommune();
    }
  }, [listCommune]);

  const getColumnPinningStorage = () => {
    const columnPinningStorage = localStorage.getItem(
      "columnPinningTransportConfiguration",
    );
    return columnPinningStorage
      ? JSON.parse(columnPinningStorage)
      : {
          left: ["mrt-row-select", "mrt-row-numbers", "code"],
          right: ["action"],
        }; // Giá trị mặc định
  };

  // Lưu columnPinning vào state
  const [columnPinning, setColumnPinning] = useState<ColumnPinningType>(
    getColumnPinningStorage(),
  );
  // Hàm lưu vào localStorage
  useEffect(() => {
    localStorage.setItem(
      "columnPinningTransportConfiguration",
      JSON.stringify(columnPinning),
    );
  }, [columnPinning]);

  useEffect(() => {
    //   startTransition(() => {
    const valuesList = Object.keys(rowSelectionDeferred);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelectionDeferred);
      setSelectIds(valuesList);
    }
    //  });
  }, [rowSelectionDeferred]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (250 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    enableRowNumbers: true,
    //add custom action buttons to top-left of top toolbar
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: true,
    getRowId: (row) => row.id?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        // left: ["mrt-row-numbers", "maVt"],
        // right: ["action"],
        ...columnPinning,
      },
      columnVisibility: { id: true, phone: false, fax: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualPagination: true,
    onColumnPinningChange: (newState) => {
      setColumnPinning(newState);
    },
    onRowSelectionChange: setRowSelection,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: "500", //conditional styling
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      rowSelection,
      columnPinning,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      style: { cursor: "pointer" },
    }),
  });

  useHotkeys([
    [
      "Enter",
      () => {
        fetchData();
      },
    ],
  ]);

  return (
    <>
      <div
        ref={headerRef}
        style={{
          width: "100%",
        }}
      >
        <LoadingOverlay
          visible={visible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
          loaderProps={{
            children: (
              <Flex direction={"column"} align="center" gap="xs">
                <Loader type="dots" size={35}></Loader>
                {/* <Title size={15}> Đang upload tài liệu...</Title> */}
                <Title size={15}> Đang xử lý....</Title>
              </Flex>
            ),
          }}
        />
        <Flex justify={"space-between"} align={"center"} w={"100%"}>
          <Grid mt={10} w={"90%"}>
            <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
              <TextInput
                placeholder="Nhập từ khóa tìm kiếm"
                defaultValue={keySearch}
                onChange={(e) => setKeySearch(e.currentTarget.value)}
                leftSection={<IconSearch color="#15aabf" />}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    fetchData();
                  }
                }}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
              <Button
                onClick={() => fetchData()}
                leftSection={<IconSearch size={14} />}
              >
                Tìm kiếm
              </Button>
            </Grid.Col>
          </Grid>
          <Button
            leftSection={<IconPlus size={16} />}
            color="green"
            onClick={() => modalsCreateTransportConfiguration()}
          >
            Thêm mới
          </Button>
        </Flex>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};

export default TransportConfiguration;
